"use strict";
/**
 * Data-set templates (plan 108): an app publishes the scope of the data it collects as an
 * AppTemplate with publication fields (`hds-dataset.json`), and a data-collection tool
 * (doctor-dashboard) imports it, keeps it in sync and turns it into a FormSpec.
 *
 * - {@link templateScopeHash}: fingerprint of what the template asks access to, so a change is
 *   detected even when the publisher forgot to bump `version`.
 * - {@link diffTemplateScope}: what changed between two versions, and the semver bump it requires.
 * - {@link templateToFormSpec}: the FormSpec a data set starts from. Permissions are always
 *   derived from the data-model, never read from the template.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.templateScope = templateScope;
exports.templateScopeHash = templateScopeHash;
exports.scopeHashMatches = scopeHashMatches;
exports.diffTemplateScope = diffTemplateScope;
exports.diffFormSpecWithTemplate = diffFormSpecWithTemplate;
exports.semverBump = semverBump;
exports.templateToFormSpec = templateToFormSpec;
exports.withAppPrivatePermissions = withAppPrivatePermissions;
exports.templateSource = templateSource;
const HDSModelInitAndSingleton_ts_1 = require("../HDSModel/HDSModelInitAndSingleton.js");
const formSpec_ts_1 = require("../cmc/formSpec.js");
const templateTypes_ts_1 = require("./templateTypes.js");
// ---------- scope hash ---------- //
/**
 * The data-collection scope of a template: each item with the kind of section it sits in
 * (permanent / recurring) and its cadence (`repeatable`, `reminder`, `required`), the custom
 * fields provisioned, the existing streams referenced with their permission levels, and
 * whether the data set opens a chat channel. Section keys, names, order and all texts are
 * excluded — reorganising sections or rewording labels is not a scope change.
 */
function templateScope(tpl) {
    const items = new Map();
    for (const s of tpl.sections) {
        for (const k of s.itemKeys ?? []) {
            items.set(k, [k, s.type, stable(cadenceOf(s.itemCustomizations?.[k]))]);
        }
    }
    return {
        items: [...items.values()].sort(byFirst),
        customFields: (tpl.customFields ?? []).map(c => [c.streamId, c.eventType]).sort(byFirst),
        existingStreamRefs: (tpl.existingStreamRefs ?? [])
            .map(r => [r.streamId, [...r.permissions].sort()])
            .sort(byFirst),
        chat: !!tpl.chat
    };
}
/** Prefix of the current scope-hash format (2.12.0+: includes `chat`). */
const SCOPE_HASH_PREFIX = 'sha256v2:';
/**
 * `sha256v2:<hex>` of {@link templateScope}. Stable under key order and section reorganisation.
 * Compare a stored hash with {@link scopeHashMatches}, which also accepts the 2.11.0 format.
 */
async function templateScopeHash(tpl) {
    return SCOPE_HASH_PREFIX + await sha256Hex(JSON.stringify(templateScope(tpl)));
}
/**
 * Does `stored` (e.g. `formSpec.source.scopeHash`) fingerprint the scope of `tpl`? Accepts the
 * current format and the 2.11.0 one (`sha256:`, computed without `chat`), so data sets imported
 * with 2.11.0 are not reported as changed after an upgrade. A chat-only change is invisible to
 * a 2.11.0 hash; it is still reported once the publisher bumps `version`.
 */
async function scopeHashMatches(tpl, stored) {
    if (stored == null)
        return false;
    if (stored.startsWith(SCOPE_HASH_PREFIX))
        return stored === await templateScopeHash(tpl);
    if (stored.startsWith('sha256:')) {
        const { chat: _chat, ...v1 } = templateScope(tpl);
        return stored === 'sha256:' + await sha256Hex(JSON.stringify(v1));
    }
    return false;
}
async function sha256Hex(text) {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
}
/** Compare two versions of a template. Pure; no model needed. */
function diffTemplateScope(prev, next) {
    const p = itemIndex(prev);
    const n = itemIndex(next);
    const added = [...n.keys()].filter(k => !p.has(k)).sort();
    const removed = [...p.keys()].filter(k => !n.has(k)).sort();
    const typeChanged = [];
    const moved = [];
    const cadenceChanged = [];
    for (const [k, a] of p) {
        const b = n.get(k);
        if (b == null)
            continue;
        if (a.type !== b.type)
            typeChanged.push(k);
        else if (a.sectionKey !== b.sectionKey)
            moved.push(k);
        if (stable(cadenceOf(a.cust)) !== stable(cadenceOf(b.cust)))
            cadenceChanged.push(k);
    }
    const pcf = new Set((prev.customFields ?? []).map(c => c.streamId + '|' + c.eventType));
    const ncf = new Set((next.customFields ?? []).map(c => c.streamId + '|' + c.eventType));
    const customFields = {
        added: [...ncf].filter(x => !pcf.has(x)).sort(),
        removed: [...pcf].filter(x => !ncf.has(x)).sort()
    };
    const pr = refIndex(prev.existingStreamRefs);
    const nr = refIndex(next.existingStreamRefs);
    const existingStreamRefs = {
        added: [...nr.keys()].filter(k => !pr.has(k)).sort(),
        removed: [...pr.keys()].filter(k => !nr.has(k)).sort(),
        permissionsChanged: [...pr.keys()].filter(k => nr.has(k) && pr.get(k) !== nr.get(k)).sort()
    };
    const featuresChanged = !!prev.chat !== !!next.chat ? ['chat'] : [];
    const textsChanged = stable(textsOf(prev)) !== stable(textsOf(next));
    const breaking = removed.length > 0 || typeChanged.length > 0 ||
        customFields.removed.length > 0 || existingStreamRefs.added.length > 0 || featuresChanged.length > 0 ||
        existingStreamRefs.removed.length > 0 || existingStreamRefs.permissionsChanged.length > 0;
    const additive = added.length > 0 || cadenceChanged.length > 0 || customFields.added.length > 0;
    const requiredBump = breaking
        ? 'major'
        : additive ? 'minor' : (textsChanged || moved.length > 0) ? 'patch' : 'none';
    const actualBump = semverBump(prev.version, next.version);
    return {
        added,
        removed,
        typeChanged: typeChanged.sort(),
        moved: moved.sort(),
        cadenceChanged: cadenceChanged.sort(),
        customFields,
        existingStreamRefs,
        featuresChanged,
        textsChanged,
        breaking,
        requiredBump,
        actualBump,
        underBumped: BUMP_RANK[actualBump] < BUMP_RANK[requiredBump]
    };
}
/**
 * What applying `tpl` would change in an existing data set: the FormSpec (including the
 * owner's own edits since import) is read as the previous template.
 *
 * `requiredBump` describes those changes, owner edits included, so it says nothing about the
 * publisher: `underBumped` is therefore computed against the stored source instead — true
 * when the template's scope hash differs from `formSpec.source.scopeHash` while its `version`
 * equals `formSpec.source.version`.
 */
async function diffFormSpecWithTemplate(formSpec, tpl) {
    const prev = {
        id: formSpec.source?.templateId ?? tpl.id,
        title: formSpec.title,
        description: formSpec.description,
        chat: !!formSpec.features?.chat,
        sections: formSpec.sections,
        customFields: formSpec.customFields,
        existingStreamRefs: formSpec.existingStreamRefs,
        consent: formSpec.consent,
        version: formSpec.source?.version,
        app: tpl.app
    };
    const diff = diffTemplateScope(prev, tpl);
    const source = formSpec.source;
    diff.underBumped = source != null && source.version != null && source.version === tpl.version &&
        !(await scopeHashMatches(tpl, source.scopeHash));
    return diff;
}
const BUMP_RANK = { none: 0, patch: 1, minor: 2, major: 3 };
/** Which semver part changed from `a` to `b`. `none` when equal, missing or not semver. */
function semverBump(a, b) {
    const pa = parseSemver(a);
    const pb = parseSemver(b);
    if (pa == null || pb == null)
        return 'none';
    if (pb[0] !== pa[0])
        return pb[0] > pa[0] ? 'major' : 'none';
    if (pb[1] !== pa[1])
        return pb[1] > pa[1] ? 'minor' : 'none';
    if (pb[2] !== pa[2])
        return pb[2] > pa[2] ? 'patch' : 'none';
    return 'none';
}
function parseSemver(v) {
    const m = typeof v === 'string' ? /^(\d+)\.(\d+)\.(\d+)$/.exec(v) : null;
    return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}
/**
 * Build the FormSpec a data set starts from.
 *
 * Permissions are derived from the sections' item keys through the data-model
 * (`authorizations.forItemKeys`, level `read`) — a template cannot ask for more. Every
 * existing-stream ref is carried read-only, and `app-private` refs are added to the
 * permissions as `read` (labelled by `ref.label`).
 *
 * @param tpl A template validated by `loadTemplate` / `loadTemplateFromUrl`.
 * @param opts.model Defaults to the initialised singleton.
 * @param opts.source Provenance to store on the FormSpec (URL import).
 */
function templateToFormSpec(tpl, opts = {}) {
    const model = opts.model ?? (0, HDSModelInitAndSingleton_ts_1.getModel)();
    const sections = tpl.sections.map(s => {
        const out = { key: s.key, type: s.type, name: structuredClone(s.name), itemKeys: [...(s.itemKeys ?? [])] };
        if (s.itemCustomizations && Object.keys(s.itemCustomizations).length > 0) {
            out.itemCustomizations = structuredClone(s.itemCustomizations);
        }
        if (s.customFieldKeys && s.customFieldKeys.length > 0)
            out.customFieldKeys = [...s.customFieldKeys];
        return out;
    });
    const formSpec = {
        version: 1,
        title: structuredClone(tpl.title),
        description: structuredClone(tpl.description),
        permissions: [],
        sections
    };
    if (tpl.consent != null)
        formSpec.consent = structuredClone(tpl.consent);
    if (tpl.chat)
        formSpec.features = { chat: true };
    if (tpl.customFields && tpl.customFields.length > 0)
        formSpec.customFields = structuredClone(tpl.customFields);
    if (tpl.existingStreamRefs && tpl.existingStreamRefs.length > 0) {
        formSpec.existingStreamRefs = tpl.existingStreamRefs.map(capToRead);
    }
    if (tpl.requiredBridges && tpl.requiredBridges.length > 0) {
        formSpec.appCustomData = { requiredBridges: [...tpl.requiredBridges] };
    }
    if (opts.source)
        formSpec.source = opts.source;
    const itemKeyIssues = (0, formSpec_ts_1.validateFormSpecItemKeys)(formSpec, model);
    const excluded = new Set(itemKeyIssues.filter(i => i.reason !== 'deprecated').map(i => i.itemKey));
    const grantable = [...new Set(sections.flatMap(s => s.itemKeys ?? []))].filter(k => !excluded.has(k));
    const permissions = model.authorizations.forItemKeys(grantable)
        .map(p => ({ streamId: p.streamId, defaultName: p.defaultName, level: p.level }));
    formSpec.permissions = withAppPrivatePermissions(permissions, formSpec.existingStreamRefs);
    return { formSpec, itemKeyIssues };
}
/**
 * `permissions` plus `read` on every `app-private` existing-stream ref not already granted
 * (named by the ref's `label`). App-private streams must be granted explicitly: nothing applies
 * existing-stream refs at acceptance, so a ref alone would be display-only. Use it wherever
 * permissions are rebuilt from item keys (e.g. an editor's `buildPermissions`), or the grant is lost.
 */
function withAppPrivatePermissions(permissions, refs) {
    const out = [...permissions];
    const granted = new Set(out.map(p => p.streamId));
    for (const ref of refs ?? []) {
        if (ref.purpose !== templateTypes_ts_1.APP_PRIVATE_PURPOSE || granted.has(ref.streamId))
            continue;
        out.push({ streamId: ref.streamId, defaultName: labelOf(ref), level: 'read' });
        granted.add(ref.streamId);
    }
    return out;
}
/** Provenance record for a FormSpec imported from a published template. */
async function templateSource(tpl, url, fetchedAt = Date.now() / 1000) {
    const source = {
        url,
        templateId: tpl.id,
        scopeHash: await templateScopeHash(tpl),
        fetchedAt
    };
    if (tpl.version != null)
        source.version = tpl.version;
    if (tpl.app?.publisher != null)
        source.publisher = tpl.app.publisher;
    return source;
}
function itemIndex(tpl) {
    const m = new Map();
    for (const s of tpl.sections) {
        for (const k of s.itemKeys ?? []) {
            m.set(k, { type: s.type, sectionKey: s.key, cust: s.itemCustomizations?.[k] });
        }
    }
    return m;
}
function refIndex(refs) {
    return new Map((refs ?? []).map(r => [r.streamId, [...r.permissions].sort().join(',')]));
}
function cadenceOf(cust) {
    if (cust == null || typeof cust !== 'object')
        return null;
    const c = cust;
    return { repeatable: c.repeatable ?? null, reminder: c.reminder ?? null, required: c.required ?? null };
}
function textsOf(tpl) {
    return {
        title: tpl.title,
        description: tpl.description,
        consent: tpl.consent ?? null,
        app: tpl.app ?? null,
        license: tpl.license ?? null,
        sections: tpl.sections.map(s => [s.key, s.name]),
        labels: tpl.sections.flatMap(s => Object.entries(s.itemCustomizations ?? {})
            .filter(([, c]) => c?.labels != null)
            .map(([k, c]) => [k, c.labels]))
            .sort(byFirst),
        refs: (tpl.existingStreamRefs ?? []).map(r => [r.streamId, r.label ?? null, r.purpose ?? null])
    };
}
/** A template is third-party input: whatever it declares, a ref is carried read-only. */
function capToRead(ref) {
    const out = structuredClone(ref);
    out.permissions = ['read'];
    return out;
}
function labelOf(ref) {
    if (ref.label == null)
        return ref.streamId;
    if (typeof ref.label === 'string')
        return ref.label;
    const l = ref.label;
    return l.en ?? Object.values(l)[0] ?? ref.streamId;
}
/** JSON with object keys sorted recursively, so equal values compare equal. */
function stable(v) {
    return JSON.stringify(v, (_k, val) => {
        if (val == null || typeof val !== 'object' || Array.isArray(val))
            return val;
        return Object.fromEntries(Object.keys(val).sort().map(k => [k, val[k]]));
    });
}
function byFirst(a, b) {
    const x = String(a[0]);
    const y = String(b[0]);
    return x < y ? -1 : x > y ? 1 : 0;
}
